import { LogoMark } from "@shared/components/Logo";
import { cn } from "@ui/lib";
import {
	FileSpreadsheetIcon,
	type LucideIcon,
	PhoneCallIcon,
	PlugIcon,
	SmartphoneIcon,
	StoreIcon,
} from "lucide-react";
import { BRAND_PATHS, type BrandIcon } from "./brand-icons";

interface Node {
	label: string;
	brand?: BrandIcon;
	icon?: LucideIcon;
}

/** The platforms by name: the Integrations page, where they are the subject. */
const NAMED_SOURCES: Node[] = [
	{ label: "Shopify", brand: "shopify" },
	{ label: "WooCommerce", brand: "woocommerce" },
	{ label: "Product feed", icon: FileSpreadsheetIcon },
	{ label: "Your API", icon: PlugIcon },
];

/** Everywhere else: what the catalog comes from, without naming store platforms. */
const SOURCES: Node[] = [
	{ label: "Your ecommerce platform", icon: StoreIcon },
	{ label: "A product feed", icon: FileSpreadsheetIcon },
	{ label: "Your API", icon: PlugIcon },
];

const CHANNELS: Node[] = [
	{ label: "Your app", icon: SmartphoneIcon },
	{ label: "WhatsApp", brand: "whatsapp" },
	{ label: "Phone calls", icon: PhoneCallIcon },
];

const STEPS = ["Understands", "Searches", "Checks", "Fills the basket"];

/**
 * How Hephon Commerce sits between a retailer's systems and its shoppers: the catalog flows in,
 * one assistant answers on every channel, and orders flow back. Dots travel the connectors so
 * the direction reads at a glance.
 */
export function FlowDiagram({
	className,
	named,
}: {
	className?: string;
	/** Name the store platforms (Shopify, WooCommerce). */
	named?: boolean;
}) {
	return (
		<figure className={cn("mk-rise", className)}>
			<div className="grid items-center gap-3 lg:grid-cols-[1fr_72px_1.15fr_72px_1fr] lg:gap-0">
				<Column
					title="Your catalog"
					nodes={named ? NAMED_SOURCES : SOURCES}
				/>
				<Connector />
				<div className="rounded-[28px] bg-(--mk-night) p-6 text-(--mk-on-night) sm:p-7">
					<div className="flex items-center gap-3">
						<LogoMark className="size-10" />
						<div>
							<p className="font-semibold text-[17px]">
								Hephon assistant
							</p>
							<p className="text-(--mk-on-night-muted) text-[13px]">
								Your rules, your languages
							</p>
						</div>
					</div>
					<ol className="mt-5 grid grid-cols-2 gap-2">
						{STEPS.map((step, i) => (
							<li
								key={step}
								className="flex items-center gap-2 rounded-xl bg-(--mk-night-2) px-3 py-2.5 text-[14px]"
							>
								<span className="font-semibold text-(--mk-signal-night) tabular-nums">
									{i + 1}
								</span>
								{step}
							</li>
						))}
					</ol>
				</div>
				<Connector />
				<Column title="Your shoppers" nodes={CHANNELS} />
			</div>
			<figcaption className="mt-6 flex items-center justify-center gap-3 text-(--mk-muted) text-[14px]">
				<span aria-hidden className="h-px w-10 bg-(--mk-line)" />
				Orders, baskets and events flow back to your platform
				<span aria-hidden className="h-px w-10 bg-(--mk-line)" />
			</figcaption>
		</figure>
	);
}

function Column({ title, nodes }: { title: string; nodes: Node[] }) {
	return (
		<div className="rounded-[28px] bg-(--mk-mist) p-5 sm:p-6">
			<p className="font-semibold text-[15px]">{title}</p>
			<ul className="mt-3 space-y-2">
				{nodes.map((node) => (
					<li
						key={node.label}
						className="flex items-center gap-3 rounded-xl bg-(--mk-white) px-3 py-2.5 text-[14px]"
					>
						<span className="grid size-7 place-items-center rounded-lg bg-(--mk-mist)">
							{node.brand ? (
								<svg
									viewBox="0 0 24 24"
									aria-hidden="true"
									className="size-3.5"
								>
									<path
										d={BRAND_PATHS[node.brand]}
										fill="currentColor"
									/>
								</svg>
							) : node.icon ? (
								<node.icon className="size-3.5" aria-hidden />
							) : null}
						</span>
						{node.label}
					</li>
				))}
			</ul>
		</div>
	);
}

/** A line with a dot travelling along it: across on wide screens, down on narrow ones. */
function Connector() {
	return (
		<div
			aria-hidden
			className="relative mx-auto h-10 w-px lg:h-px lg:w-full"
		>
			<span className="absolute inset-0 bg-(--mk-line)" />
			<span className="mk-travel absolute top-0 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-(--mk-signal) lg:top-1/2 lg:left-0" />
		</div>
	);
}
